import { idNumber } from '../ids.js';
import { edgeId } from '../edges.js';

/**
 * Merge topology vertices that occupy the same position.
 *
 * Phase 2, Milestone E. `toEditableMesh` captures a THREE geometry's render
 * buffer verbatim, and THREE duplicates vertices across hard edges and UV
 * seams — a unit box arrives as 24 vertices at 8 positions. Nothing in the
 * topology then says those 24 are 8 corners, so no edge is shared by two
 * faces, every component looks open, and `orientFacesOutside` refuses the mesh
 * with "cannot orient open component". Milestone D found the same wall from the
 * other side: smoothing a box does nothing, because no vertex is shared for a
 * normal to be averaged across.
 *
 * This is deliberately the CONSERVATIVE weld: it merges what is already
 * coincident and nothing else. Collapsing geometry that is merely *close* is a
 * different intent with different risk, and belongs to a separately named
 * `mergeByDistance` rather than to a tolerance quietly widened here. An agent
 * asking to weld a topology must not silently get its detail simplified.
 *
 * Pure: the input topology is never mutated. The caller commits the returned
 * candidate, which is what lets the command be atomic.
 */

const DEFAULT_TOLERANCE = 1e-6;

function validate(topology, tolerance) {
  if (!topology || !Array.isArray(topology.vertices) || !Array.isArray(topology.faces)) {
    throw new Error('weldVertices requires topology vertices and faces arrays');
  }
  if (topology.vertices.length === 0 || topology.faces.length === 0) {
    throw new Error('weldVertices requires a non-empty vertices and faces array');
  }
  if (typeof tolerance !== 'number' || !Number.isFinite(tolerance) || tolerance <= 0) {
    throw new Error(`weldVertices tolerance must be a finite number > 0, got ${JSON.stringify(tolerance)}`);
  }
  const seen = new Set();
  for (const vertex of topology.vertices) {
    if (!vertex || typeof vertex.id !== 'string' || seen.has(vertex.id)
      || !Array.isArray(vertex.position) || vertex.position.length !== 3
      || !vertex.position.every(Number.isFinite)) {
      throw new Error('weldVertices found a malformed or non-finite vertex');
    }
    seen.add(vertex.id);
  }
  for (const face of topology.faces) {
    if (!face || typeof face.id !== 'string' || !Array.isArray(face.vertexIds)
      || face.vertexIds.length !== 3 || !face.vertexIds.every((id) => seen.has(id))) {
      throw new Error(`weldVertices found a malformed face '${face?.id ?? '?'}'`);
    }
  }
}

/**
 * Assign every vertex to a surviving vertex within `tolerance` of it.
 *
 * Deliberately NOT the connected components of "distance <= tolerance". That
 * relation is not transitive, and its transitive closure breaks the only
 * promise this operation makes. Three points at 0, 0.75e-6 and 1.5e-6 with a
 * 1e-6 tolerance are pairwise-chained but span 1.5e-6, so connected components
 * merge all three and the outer vertex moves half again as far as the caller
 * allowed. A long enough chain of near points collapses arbitrarily far. For a
 * weld whose whole character is "merge what is already coincident and nothing
 * else", that is the wrong answer at any chain length.
 *
 * Instead: walk the vertices in numeric-id order and let each either join the
 * lowest-id survivor already within tolerance, or become a survivor itself.
 * Every merged vertex is then within `tolerance` of the vertex it merged into,
 * by construction rather than by hope — asserted directly in the tests.
 *
 * Walking in id order rather than array order is what makes the result
 * independent of how the vertices happen to be stored, so two viewers weld to
 * the same ids. The spatial hash at `tolerance` granularity narrows the search
 * and never decides membership: the distance test is exact, so the boundary is
 * not quantised by where the grid fell. A point within `tolerance` differs by
 * at most `tolerance` on each axis, so the 27-cell neighbourhood is sufficient.
 */
function assignSurvivors(vertices, tolerance) {
  // Numbers are computed once rather than per comparison and per candidate:
  // the survivor scan below runs this for every candidate in 27 cells, which
  // is the hot path on a dense mesh.
  //
  // The id STRING breaks ties, and that is load-bearing rather than tidy.
  // `parseTopology` accepts any non-empty string id, and `idNumber` reports 0
  // for anything that is not `v<n>` — so a mesh authored with descriptive ids
  // would have every vertex ranked equal, the sort would fall back to array
  // order, and which vertex survived would depend on how the topology happened
  // to be stored. Number then string is a total order over any id set.
  const numbers = vertices.map((vertex) => idNumber(/^v(\d+)$/, vertex.id, 'weldVertices'));
  const ranksBelow = (a, b) => (numbers[a] !== numbers[b]
    ? numbers[a] < numbers[b]
    : vertices[a].id < vertices[b].id);

  const order = vertices
    .map((_, index) => index)
    .sort((a, b) => (ranksBelow(a, b) ? -1 : (ranksBelow(b, a) ? 1 : 0)));

  const cellOf = (position) => position.map((value) => Math.floor(value / tolerance));
  // Only survivors are indexed, so a candidate is never matched against a
  // vertex that has itself already been merged away.
  const survivorBuckets = new Map();
  const toleranceSquared = tolerance * tolerance;
  const survivingIndexOf = new Array(vertices.length);

  for (const index of order) {
    const position = vertices[index].position;
    const [cx, cy, cz] = cellOf(position);

    let best = -1;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          const list = survivorBuckets.get(`${cx + dx},${cy + dy},${cz + dz}`);
          if (!list) continue;
          for (const survivor of list) {
            const other = vertices[survivor].position;
            const ex = position[0] - other[0];
            const ey = position[1] - other[1];
            const ez = position[2] - other[2];
            if (ex * ex + ey * ey + ez * ez > toleranceSquared) continue;
            if (best < 0 || ranksBelow(survivor, best)) best = survivor;
          }
        }
      }
    }

    if (best >= 0) {
      survivingIndexOf[index] = best;
      continue;
    }
    survivingIndexOf[index] = index;
    const key = `${cx},${cy},${cz}`;
    let list = survivorBuckets.get(key);
    if (!list) {
      list = [];
      survivorBuckets.set(key, list);
    }
    list.push(index);
  }

  return survivingIndexOf;
}

/**
 * Carry explicit edge marks across the weld.
 *
 * An edge id is an opaque encoding of its endpoint pair — `edgeId` has two
 * forms and is not reliably parseable — so the pair is recovered from the
 * original faces rather than by splitting the string. A mark whose endpoints
 * merged into one another no longer names an edge and is dropped; so is one
 * whose edge no longer exists among the surviving faces.
 */
function remapExplicitEdges(topology, weldedFaces, survivingIdOf) {
  const endpointsOf = new Map();
  for (const face of topology.faces) {
    for (let i = 0; i < face.vertexIds.length; i++) {
      const a = face.vertexIds[i];
      const b = face.vertexIds[(i + 1) % face.vertexIds.length];
      endpointsOf.set(edgeId(a, b), [a, b]);
    }
  }
  const surviving = new Set();
  for (const face of weldedFaces) {
    for (let i = 0; i < face.vertexIds.length; i++) {
      surviving.add(edgeId(face.vertexIds[i], face.vertexIds[(i + 1) % face.vertexIds.length]));
    }
  }

  const kept = new Set();
  for (const id of topology.explicitEdgeIds) {
    const endpoints = endpointsOf.get(id);
    if (!endpoints) continue;
    const a = survivingIdOf.get(endpoints[0]) ?? endpoints[0];
    const b = survivingIdOf.get(endpoints[1]) ?? endpoints[1];
    if (a === b) continue;
    const mapped = edgeId(a, b);
    if (surviving.has(mapped)) kept.add(mapped);
  }
  return [...kept].sort();
}

/**
 * @param topology  canonical topology, not mutated
 * @param tolerance coincidence radius in the mesh's own local units
 * @returns { topology, mergedVertexCount, removedFaceCount, before, after, changed }
 */
export function weldVertices(topology, { tolerance = DEFAULT_TOLERANCE } = {}) {
  validate(topology, tolerance);

  const vertices = topology.vertices;
  const survivingIndexOf = assignSurvivors(vertices, tolerance);

  const survivingIdOf = new Map();
  vertices.forEach((vertex, index) => {
    survivingIdOf.set(vertex.id, vertices[survivingIndexOf[index]].id);
  });

  const keptIds = new Set(survivingIdOf.values());
  const weldedVertices = vertices
    .filter((vertex) => keptIds.has(vertex.id))
    // The survivor keeps its OWN position rather than a group average. At a
    // coincidence tolerance an average is numerically indistinguishable, and
    // keeping an existing value avoids inventing float coordinates that were
    // never in the mesh.
    .map((vertex) => ({ id: vertex.id, position: [...vertex.position] }));

  const weldedFaces = [];
  let removedFaceCount = 0;
  for (const face of topology.faces) {
    const mapped = face.vertexIds.map((id) => survivingIdOf.get(id));
    // A face whose corners merged into each other has collapsed to an edge or a
    // point. It is not a face any more and cannot be rendered, so it goes.
    if (new Set(mapped).size !== 3) {
      removedFaceCount++;
      continue;
    }
    weldedFaces.push({ id: face.id, vertexIds: mapped });
  }

  if (weldedFaces.length === 0) {
    throw new Error('weldVertices would remove every face — refusing to reduce the mesh to nothing');
  }

  const mergedVertexCount = vertices.length - weldedVertices.length;

  return {
    topology: {
      // Counters are NOT rewound. They are part of the topology's identity and
      // determine every id minted from here on; rewinding one would let a later
      // operation mint an id that collides with a face this weld kept.
      nextVertexId: topology.nextVertexId,
      nextFaceId: topology.nextFaceId,
      vertices: weldedVertices,
      faces: weldedFaces,
      ...(topology.explicitEdgeIds === undefined
        ? {}
        : { explicitEdgeIds: remapExplicitEdges(topology, weldedFaces, survivingIdOf) }),
    },
    mergedVertexCount,
    removedFaceCount,
    before: { vertexCount: vertices.length, faceCount: topology.faces.length },
    after: { vertexCount: weldedVertices.length, faceCount: weldedFaces.length },
    changed: mergedVertexCount > 0 || removedFaceCount > 0,
  };
}

export { DEFAULT_TOLERANCE as WELD_DEFAULT_TOLERANCE };
